/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Image Color Transform - Color transformations for different print types
 * Updated: 2026-01-09
 */

/**
 * Apply laser print color transformation (gold color mapping)
 * Converts logo to gold color (#DEB887) while preserving detail through luminance-to-alpha mapping
 * ALGORITHM MATCHES cupMockupService.ts processLogoColor() exactly
 */
export const applyLaserTransform = (
  imageBase64: string,
  whiteThreshold: number = 220,
  detailThreshold: number = 50
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');

        // Draw original image
        ctx.drawImage(img, 0, 0);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Target Color: #DEB887 -> RGB(222, 184, 135) - Gold/tan color for laser engraving
        const tr = 222, tg = 184, tb = 135;

        // White background removal threshold (fixed at 220)
        const whiteThresh = 220;

        // Detail threshold (user adjustable, 0-255, default 50)
        // Controls print intensity threshold (median-based, aggressive detail preservation)
        // 0 = all non-white pixels print (max detail)
        // 50 = medium tone threshold (default, median-based)
        // 100 = max clamp (high contrast)
        // 255 = only pure black prints (minimum detail)
        const detailIntensity = Math.max(0, Math.min(255, detailThreshold));

        // Process each pixel - INTENSITY MAPPING + BINARY CONVERSION
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          if (a < 10) {
            // Skip already transparent pixels
            data[i] = 0;
            data[i + 1] = 0;
            data[i + 2] = 0;
            data[i + 3] = 0;
            continue;
          }

          // 1. Calculate luminance (brightness) of original pixel: 0 (Black) -> 255 (White)
          const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

          // 2. Remove white background
          // If pixel is very bright and not transparent -> Consider as background -> Remove
          if (luminance > whiteThresh && a > 200) {
            data[i] = 0;
            data[i + 1] = 0;
            data[i + 2] = 0;
            data[i + 3] = 0;
            continue;
          }

          // 3. Map luminance to intensity (Detail Preservation)
          // Darker pixels (low luminance) -> Higher intensity
          // Lighter gray pixels (high luminance) -> Lower intensity
          // Invert brightness: Black (0) -> Intensity 1.0, White (255) -> Intensity 0.0
          const intensity = 1 - (luminance / 255);

          // 4. Apply detail threshold to determine if pixel prints
          // detailIntensity/255 is the threshold (0.0 to 1.0)
          // Only print if intensity >= threshold
          const threshold = detailIntensity / 255;

          data[i] = tr;      // R = gold
          data[i + 1] = tg;  // G = gold
          data[i + 2] = tb;  // B = gold

          // BINARY CONVERSION: Full gold or transparent
          if (intensity >= threshold) {
            // Intensity high enough (dark enough) → Print as full gold
            data[i + 3] = 255;
          } else {
            // Intensity too low (too light) → Fully transparent
            data[i + 3] = 0;
          }
        }

        ctx.putImageData(imageData, 0, 0);
        console.log(`✅ [LaserTransform] Applied with whiteThresh: ${whiteThresh}, detailThresh: ${detailThreshold} (intensity: ${(detailIntensity/255).toFixed(3)})`);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => reject(new Error('Failed to load image for laser transform'));
    img.src = imageBase64;
  });
};

/**
 * Apply silk print color transformation (preserve colors)
 * Enhances saturation for vibrant silk screen printing
 */
export const applySilkTransform = (
  imageBase64: string
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');

        // Draw original image
        ctx.drawImage(img, 0, 0);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Enhance saturation for silk printing
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          // Convert to HSV to adjust saturation
          const [h, s, v] = rgbToHsv(r, g, b);
          const enhancedS = Math.min(1, s * 1.15); // +15% saturation
          const [newR, newG, newB] = hsvToRgb(h, enhancedS, v);

          data[i] = newR;
          data[i + 1] = newG;
          data[i + 2] = newB;
        }

        // Apply brightness adjustment
        adjustBrightnessContrast(data, 1.05, 5); // brightness +5%, contrast +5

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => reject(new Error('Failed to load image for silk transform'));
    img.src = imageBase64;
  });
};

/**
 * Adjust brightness and contrast of pixel data
 */
function adjustBrightnessContrast(
  data: Uint8ClampedArray,
  brightness: number = 1,
  contrast: number = 0
) {
  for (let i = 0; i < data.length; i += 4) {
    for (let j = 0; j < 3; j++) {
      // Apply contrast (around 128)
      let value = (data[i + j] - 128) * (1 + contrast / 100) + 128;
      // Apply brightness
      value *= brightness;
      // Clamp to 0-255
      data[i + j] = Math.max(0, Math.min(255, value));
    }
  }
}

/**
 * Convert RGB to HSV
 */
function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = max === 0 ? 0 : delta / max;
  let v = max;

  if (delta !== 0) {
    if (max === r) {
      h = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
    } else if (max === g) {
      h = ((b - r) / delta + 2) / 6;
    } else {
      h = ((r - g) / delta + 4) / 6;
    }
  }

  return [h, s, v];
}

/**
 * Convert HSV to RGB
 */
function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const c = v * s;
  const hh = (h * 6) % 6;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const m = v - c;

  let r = 0, g = 0, b = 0;

  if (hh < 1) {
    r = c;
    g = x;
  } else if (hh < 2) {
    r = x;
    g = c;
  } else if (hh < 3) {
    g = c;
    b = x;
  } else if (hh < 4) {
    g = x;
    b = c;
  } else if (hh < 5) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}

/**
 * Apply black print transformation - Convert to pure black (RGB 0,0,0) for factory printing
 * Requirements from ICD.ai file analysis:
 * - Only black color (DocumentProcessColors: Black)
 * - No gradients, no color transitions
 * - Pure black RGB(0,0,0) - no semi-transparent gray
 * - Remove white background
 * - Preserve detail through alpha channel only
 * - Apply detail threshold filtering for consistency with preview
 *
 * This ensures the output is ready for factory conversion to vector (AI/SVG)
 * and matches the preview threshold settings.
 */
export const applyBlackPrintTransform = (
  imageBase64: string,
  detailThreshold: number = 50
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');

        // Draw original image
        ctx.drawImage(img, 0, 0);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // White background removal threshold
        // 220 = Balanced threshold to remove white/light backgrounds while preserving content
        const whiteThreshold = 220;

        // Detail threshold (user adjustable, 0-255, default 50)
        // Applied same as laser transform for consistency
        const detailIntensity = Math.max(0, Math.min(255, detailThreshold));
        const threshold = detailIntensity / 255;

        // Process each pixel: Convert to PURE BLACK RGB(0,0,0) with detail filtering
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          // Skip already transparent pixels
          if (a < 10) {
            data[i] = 0;
            data[i + 1] = 0;
            data[i + 2] = 0;
            data[i + 3] = 0;
            continue;
          }

          // Calculate luminance (brightness)
          const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

          // Remove white background first
          if (luminance > whiteThreshold && a > 200) {
            // White/light background → Remove completely
            data[i] = 0;
            data[i + 1] = 0;
            data[i + 2] = 0;
            data[i + 3] = 0;
            continue;
          }

          // BINARY CONVERSION: Pure Black or Transparent (No Layers)
          // Apply detail threshold filtering (same logic as laser transform)
          // Map luminance to intensity: Black (0) → Intensity 1.0, White (255) → Intensity 0.0
          const intensity = 1 - (luminance / 255);

          data[i] = 0;       // R
          data[i + 1] = 0;   // G
          data[i + 2] = 0;   // B

          // Apply detail threshold: only print if intensity >= threshold
          if (intensity >= threshold) {
            // Dark enough → FULL BLACK (100% opaque)
            data[i + 3] = 255;
          } else {
            // Too light based on threshold → Transparent
            data[i + 3] = 0;
          }
        }

        ctx.putImageData(imageData, 0, 0);

        console.log(`✅ [BlackPrintTransform] Converted to pure black RGB(0,0,0) with detailThreshold=${detailThreshold}`);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => reject(new Error('Failed to load image for black print transform'));
    img.src = imageBase64;
  });
};
